import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { FaClock, FaEnvelope, FaFacebook, FaFacebookF, FaInstagram, FaChurch, FaGlobeAmericas, FaUsers, FaNewspaper, FaTv, FaHeart, FaHandHoldingHeart, FaBook, FaCross, FaGlobe } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import Escudo from './Escudo';
import './Footer.css';

const FACEBOOK_URL_DIOCESIS = 'https://www.facebook.com/Diocesisdeipialesoficial/';

const Footer = () => {
  const { t } = useLanguage();

  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      try {
        const response = await api.get('/configuracion');
        return response.data;
      } catch {
        return {};
      }
    },
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  const facebookUrl = (config.facebook_url && !config.facebook_url.includes('Guachucal')) ? config.facebook_url : FACEBOOK_URL_DIOCESIS;
  const instagramUrl = config.instagram_url || '#';
  const tiktokUrl = config.tiktok_url || '#';

  const handleLinkClick = () => {
    // Scroll al top cuando se hace clic en un enlace del footer
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <footer className="footer">
      {/* Primero: franja blanca con logo liturgia, horario y redes sociales */}
      <div className="footer-banner-blanco">
        <div className="footer-banner-container">
          <div className="footer-horario footer-horario-blanco">
            <h5 className="footer-horario-titulo">
              <span className="footer-horario-icono"><FaClock /></span>
              Horarios de Atención
            </h5>
            <p>Lunes-Viernes: 8:00am - 12:00pm - 2:00pm - 6:00pm</p>
          </div>
          <img src="/images/logo_liturgia_transparente.png" alt="Logo Liturgia - Diócesis de Ipiales" className="footer-logo-image" />
          <div className="footer-banner-redes">
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="footer-redes-link" aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="footer-redes-link" aria-label="Instagram">
              <FaInstagram />
            </a>
            <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="footer-redes-link" aria-label="TikTok">
              <SiTiktok />
            </a>
          </div>
        </div>
      </div>

      {/* Luego: parte azul con enlaces */}
      <div className="footer-azul">
      <div className="footer-container">
        <div className="footer-links-section">
          <div className="footer-links-column">
            <h4 className="footer-column-title">Enlaces Externos</h4>
            <ul className="footer-links">
              <li><a href="https://www.cec.org.co" target="_blank" rel="noopener noreferrer"><FaChurch className="footer-link-icon" /> Conferencia Episcopal de Colombia</a></li>
              <li><a href="https://www.celam.org" target="_blank" rel="noopener noreferrer"><FaGlobeAmericas className="footer-link-icon" /> Consejo Episcopal Latinoamericano</a></li>
              <li><a href="https://www.synod.va" target="_blank" rel="noopener noreferrer"><FaUsers className="footer-link-icon" /> Sínodo de la Sinodalidad</a></li>
              <li><a href="https://www.vaticannews.va" target="_blank" rel="noopener noreferrer"><FaNewspaper className="footer-link-icon" /> Vatican News</a></li>
              <li><a href="https://www.cristovision.tv" target="_blank" rel="noopener noreferrer"><FaTv className="footer-link-icon" /> Cristovisión</a></li>
            </ul>
          </div>

          <div className="footer-links-column">
            <h4 className="footer-column-title">Pastorales</h4>
            <ul className="footer-links">
              <li><Link to="/familias" onClick={handleLinkClick}><FaHeart className="footer-link-icon" /> Pastoral Familiar</Link></li>
              <li><Link to="/juventud" onClick={handleLinkClick}><FaUsers className="footer-link-icon" /> Pastoral Juvenil</Link></li>
              <li><Link to="/caridad" onClick={handleLinkClick}><FaHandHoldingHeart className="footer-link-icon" /> Caridad y Pastoral Social</Link></li>
              <li><Link to="/formacion" onClick={handleLinkClick}><FaBook className="footer-link-icon" /> Catequesis</Link></li>
              <li><Link to="/liturgia" onClick={handleLinkClick}><FaCross className="footer-link-icon" /> Liturgia</Link></li>
              <li><Link to="/misiones" onClick={handleLinkClick}><FaGlobe className="footer-link-icon" /> Misiones</Link></li>
            </ul>
          </div>

          <div className="footer-links-column">
            <div className="footer-social">
              <h4>Síguenos</h4>
              <div className="social-links">
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
                  <FaFacebook /> Facebook
                </a>
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                  <FaInstagram /> Instagram
                </a>
                <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="social-link" aria-label="TikTok">
                  <SiTiktok /> TikTok
                </a>
                <p className="social-note">
                  Visita nuestras redes para ver videos, publicaciones y más información actualizada.
                </p>
              </div>
            </div>
          </div>

          <div className="footer-links-column">
            <h4>Notificaciones judiciales</h4>
            <p className="footer-nota">
              <strong>Notificaciones judiciales:</strong><br />
              <a href="mailto:oficinajuridicadiocesisipiales@gmail.com" className="footer-email-link">
                <FaEnvelope /> oficinajuridicadiocesisipiales@gmail.com
              </a>
            </p>
            <p className="footer-nota-small">
              <em>Para cualquier asunto judicial diríjase únicamente a este correo.</em>
            </p>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <div className="footer-bottom-escudo">
            <Escudo variant="footer" />
          </div>
          <div className="footer-bottom-texto">
            <p>&copy; {new Date().getFullYear()} <strong>Diócesis de Ipiales</strong> - Todos los derechos reservados</p>
            <div className="footer-bottom-links">
              <Link to="/acerca" onClick={handleLinkClick}>Acerca de la Diócesis</Link>
              <Link to="/noticias" onClick={handleLinkClick}>Noticias</Link>
              <Link to="/contacto" onClick={handleLinkClick}>Contáctenos</Link>
              <Link to="/mapa-sitio" onClick={handleLinkClick}>Mapa del sitio</Link>
              <Link to="/politica-privacidad" onClick={handleLinkClick}>Política de Privacidad</Link>
              <Link to="/tratamiento-datos" onClick={handleLinkClick}>Tratamiento de Datos</Link>
            </div>
            <p className="footer-credit">Desarrollada por <a href="https://camsoft.com.co" target="_blank" rel="noopener noreferrer">camsoft.com.co</a></p>
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
};

export default Footer;


